import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Gift, TrendingUp, Users, Sparkles } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Separator } from '@/components/ui/separator';
import { LoyaltyConfigComponent, type LoyaltyConfig } from './ConfigComponent';
import { useLoyaltyDashboard } from './hooks';
import { LoyaltyParticipantsTable } from './LoyaltyParticipantsTable';
import { usePlugin, useUpdatePluginConfig } from '../usePlugins';
import { toast } from 'sonner';

const LOYALTY_SLUG = 'fidelidade';

const MetricCard = ({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof Gift;
  label: string;
  value: string;
}) => (
  <Card className="shadow-sm">
    <CardContent className="p-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-md bg-[#EAEFFE] text-[#574BFB]">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs text-muted-foreground">{label}</p>
          <p className="text-xl font-semibold text-foreground">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

export const LoyaltyPageComponent = () => {
  const { plugin, isEnabled, config } = usePlugin(LOYALTY_SLUG);
  const { data: dashboard, isLoading: dashboardLoading } = useLoyaltyDashboard();
  const updateConfig = useUpdatePluginConfig();

  if (!plugin) {
    return (
      <Card className="shadow-sm">
        <CardContent className="p-6 text-sm text-muted-foreground">
          Plugin não encontrado no catálogo. Contate o suporte.
        </CardContent>
      </Card>
    );
  }

  const effectiveConfig: LoyaltyConfig = {
    points_per_real: (config.points_per_real as number) ?? 1,
    minimum_sale_value: (config.minimum_sale_value as number) ?? 0,
  };

  const handleSaveConfig = (next: LoyaltyConfig) => {
    updateConfig.mutate(
      { slug: LOYALTY_SLUG, config: next as unknown as Record<string, unknown> },
      {
        onSuccess: () => {
          toast.success('Configuração salva');
        },
        onError: (err) => {
          toast.error(`Erro ao salvar: ${err instanceof Error ? err.message : 'desconhecido'}`);
        },
      },
    );
  };

  if (!isEnabled) {
    return (
      <div className="space-y-4">
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base font-medium">
              <Gift className="h-4 w-4 text-[#574BFB]" />
              Programa de Fidelidade
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              O plugin está desativado. Ative em Configurações → Plugins para começar a creditar pontos aos seus clientes.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Metrics */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {dashboardLoading || !dashboard?.metrics ? (
          <>
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
            <Skeleton className="h-20" />
          </>
        ) : (
          <>
            <MetricCard
              icon={Sparkles}
              label="Pontos distribuídos"
              value={dashboard.metrics.total_distributed.toLocaleString('pt-BR')}
            />
            <MetricCard
              icon={TrendingUp}
              label="Saldo ativo total"
              value={dashboard.metrics.active_balance.toLocaleString('pt-BR')}
            />
            <MetricCard
              icon={Users}
              label="Clientes participantes"
              value={dashboard.metrics.participating_customers.toLocaleString('pt-BR')}
            />
            <MetricCard
              icon={Gift}
              label="Ativo desde"
              value={
                dashboard.metrics.activated_at
                  ? format(new Date(dashboard.metrics.activated_at), "dd 'de' MMM", { locale: ptBR })
                  : '—'
              }
            />
          </>
        )}
      </div>

      {/* Participants (with search + pagination + history drawer) */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-medium">Clientes participantes</CardTitle>
        </CardHeader>
        <CardContent>
          <LoyaltyParticipantsTable />
        </CardContent>
      </Card>

      {/* Config form */}
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base font-medium">Regras de pontuação</CardTitle>
        </CardHeader>
        <CardContent>
          <LoyaltyConfigComponent
            config={effectiveConfig}
            onSave={handleSaveConfig}
            isSaving={updateConfig.isPending}
          />
        </CardContent>
      </Card>

      <Separator />
      <p className="text-xs text-muted-foreground">
        Os pontos são creditados automaticamente quando uma venda é marcada como paga. Cancelamentos e reembolsos estornam o saldo na mesma proporção. O crédito pode levar até 1 minuto para aparecer após o pagamento.
      </p>
    </div>
  );
};
